import React, { useState, useRef, useCallback, useEffect } from "react";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import "../partsCatalogPartSearch/partSearchTab.css";
import { fetchAllActiveAttributes } from "../../graphql/partsCatalogQueries";

const emptyForm = () => ({
  modelNumber: "",
  description: "",
  brandId: "",
  brandName: "",
  productTypeId: "",
  productType: "",
  attribute: "",
  value: "",
});

const MOCK_RESULT_ROWS = [
  {
    modelNumber: "9543",
    description: "JIG SAW",
    brandId: "0826",
    brandName: "PORTER",
    productTypeId: "0744430",
    productType: "Saw*",
    attribute: "",
  },
  {
    modelNumber: "9544",
    description: "CIRCULAR SAW",
    brandId: "0826",
    brandName: "PORTER",
    productTypeId: "0744431",
    productType: "Saw*",
    attribute: "",
  },
  {
    modelNumber: "9545",
    description: "RECIP SAW",
    brandId: "0827",
    brandName: "PORTER",
    productTypeId: "0744432",
    productType: "Saw*",
    attribute: "",
  },
  {
    modelNumber: "9600",
    description: "DRILL DRIVER",
    brandId: "0900",
    brandName: "CRAFTSMAN",
    productTypeId: "0800100",
    productType: "Drill*",
    attribute: "",
  },
  {
    modelNumber: "9601",
    description: "HAMMER DRILL",
    brandId: "0900",
    brandName: "CRAFTSMAN",
    productTypeId: "0800101",
    productType: "Drill*",
    attribute: "",
  },
  {
    modelNumber: "9700",
    description: "BELT SANDER",
    brandId: "0826",
    brandName: "PORTER",
    productTypeId: "0744500",
    productType: "Sander*",
    attribute: "",
  },
];

export default function ModelSearchTab() {
  const [criteriaOpen, setCriteriaOpen] = useState(true);
  const [form, setForm] = useState(() => ({
    ...emptyForm(),
    modelNumber: "954*",
  }));
  const [resultRows, setResultRows] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [attributesError, setAttributesError] = useState(null);
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [attributesFetchAttempted, setAttributesFetchAttempted] =
    useState(false);
  const attributesInFlightRef = useRef(false);
  const attributesLoadedOkRef = useRef(false);

  const loadAttributesFromDropdown = useCallback(async () => {
    if (attributesInFlightRef.current) return;
    if (attributesLoadedOkRef.current) return;

    attributesInFlightRef.current = true;
    setAttributesLoading(true);
    setAttributesError(null);
    try {
      const list = await fetchAllActiveAttributes();
      setAttributes(list);
      attributesLoadedOkRef.current = true;
    } catch (e) {
      setAttributes([]);
      setAttributesError(e?.message || "Failed to load attributes");
      console.error(e);
    } finally {
      attributesInFlightRef.current = false;
      setAttributesLoading(false);
      setAttributesFetchAttempted(true);
    }
  }, []);

  useEffect(() => {
    if (!criteriaOpen) return;
    loadAttributesFromDropdown();
  }, [criteriaOpen, loadAttributesFromDropdown]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    setForm(emptyForm());
    setResultRows([]);
  };

  const handleSearch = () => {
    setResultRows(MOCK_RESULT_ROWS);
  };

  return (
    <div className="partSearchTab">
      <section className="pscSection">
        <button
          type="button"
          className="pscSectionHeader"
          onClick={() => setCriteriaOpen((o) => !o)}
          aria-expanded={criteriaOpen}
        >
          <span className="pscSectionTitle">Model Search Criteria</span>
          <span className="pscSectionToggle" aria-hidden>
            {criteriaOpen ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )}
          </span>
        </button>
        {criteriaOpen && (
          <div className="pscSectionBody">
            <div className="pscCriteriaLayout">
              <div className="pscFieldGrid">
                <div className="pscField">
                  <label htmlFor="msc-modelNumber">Model Number:</label>
                  <input
                    id="msc-modelNumber"
                    className="pscInput"
                    type="text"
                    value={form.modelNumber}
                    onChange={(e) => setField("modelNumber", e.target.value)}
                  />
                </div>
                <div className="pscField">
                  <label htmlFor="msc-description">Description:</label>
                  <input
                    id="msc-description"
                    className="pscInput"
                    type="text"
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </div>
                <div className="pscField">
                  <label htmlFor="msc-brandId">Brand ID:</label>
                  <input
                    id="msc-brandId"
                    className="pscInput"
                    type="text"
                    value={form.brandId}
                    onChange={(e) => setField("brandId", e.target.value)}
                  />
                </div>
                <div className="pscField">
                  <label htmlFor="msc-brandName">Brand Name:</label>
                  <input
                    id="msc-brandName"
                    className="pscInput"
                    type="text"
                    value={form.brandName}
                    onChange={(e) => setField("brandName", e.target.value)}
                  />
                </div>
                <div className="pscField">
                  <label htmlFor="msc-productTypeId">Product Type ID:</label>
                  <input
                    id="msc-productTypeId"
                    className="pscInput"
                    type="text"
                    value={form.productTypeId}
                    onChange={(e) => setField("productTypeId", e.target.value)}
                  />
                </div>
                <div className="pscField">
                  <label htmlFor="msc-productType">Product Type:</label>
                  <input
                    id="msc-productType"
                    className="pscInput"
                    type="text"
                    value={form.productType}
                    onChange={(e) => setField("productType", e.target.value)}
                  />
                </div>
                <div className="pscField">
                  <label htmlFor="msc-attribute">Attribute:</label>
                  <select
                    id="msc-attribute"
                    className="pscInput"
                    value={form.attribute}
                    onChange={(e) => setField("attribute", e.target.value)}
                    onFocus={loadAttributesFromDropdown}
                    disabled={attributesLoading}
                    aria-busy={attributesLoading}
                    title={attributesError || undefined}
                  >
                    <option value="">
                      {attributesLoading
                        ? "Loading attributes…"
                        : attributesError
                          ? "-- Error — focus to retry --"
                          : attributesFetchAttempted
                            ? "-- Select --"
                            : "-- Select — click to load attributes --"}
                    </option>
                    {attributes.map((a) => {
                      const idStr = String(a.id ?? "");
                      const nameStr =
                        a.name != null && a.name !== "" ? String(a.name) : "";
                      const label =
                        idStr && nameStr
                          ? `${idStr}, ${nameStr}`
                          : idStr || nameStr || "—";
                      return (
                        <option key={idStr || label} value={idStr}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="pscField">
                  <label htmlFor="msc-value">Value:</label>
                  <input
                    id="msc-value"
                    className="pscInput"
                    type="text"
                    value={form.value}
                    onChange={(e) => setField("value", e.target.value)}
                  />
                </div>
              </div>
              <div className="pscActions">
                <button type="button" className="pscBtnPrimary" onClick={handleSearch}>
                  Search
                </button>
                <button type="button" className="pscBtnPrimary" onClick={handleClear}>
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="pscSection">
        <div className="pscSectionHeader pscSectionHeaderStatic">
          <span className="pscSectionTitle">Model Search Result</span>
          <button
            type="button"
            className="pscGearBtn"
            aria-label="Result settings"
            title="Settings"
          >
            <SettingsIcon fontSize="small" />
          </button>
        </div>
        <div
          className={`pscSectionBody pscResultBody ${
            resultRows.length > 0 ? "pscResultBodyTable" : ""
          }`}
        >
          {resultRows.length === 0 ? (
            <p className="pscNoData">No Data Found</p>
          ) : (
            <div className="pscTableWrap">
              <table className="pscTable">
                <thead>
                  <tr>
                    <th>Model Number</th>
                    <th>Description</th>
                    <th>Brand ID</th>
                    <th>Brand Name</th>
                    <th>Product Type ID</th>
                    <th>Product Type</th>
                    <th>Attribute</th>
                  </tr>
                </thead>
                <tbody>
                  {resultRows.map((row, idx) => (
                    <tr key={`${row.modelNumber}-${idx}`}>
                      <td>
                        <button
                          type="button"
                          className="pscModelLink"
                          onClick={() => {
                            /* Model drill-down when API is available */
                          }}
                        >
                          {row.modelNumber}
                        </button>
                      </td>
                      <td>{row.description}</td>
                      <td>{row.brandId}</td>
                      <td>{row.brandName}</td>
                      <td>{row.productTypeId}</td>
                      <td>{row.productType}</td>
                      <td>{row.attribute}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
